import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  verifyOTP: (email, otp) => axios.post(`${API}/auth/verify-otp`, { email, otp }),

  // Patients
  getPatients: () => axios.get(`${API}/patients`, getAuthHeaders()),
  getPatient: (id) => axios.get(`${API}/patients/${id}`, getAuthHeaders()),
  createPatient: (data) => axios.post(`${API}/patients`, data, getAuthHeaders()),
  updatePatient: (id, data) => axios.put(`${API}/patients/${id}`, data, getAuthHeaders()),
  getPatientBiometrics: (id, days = 30) => axios.get(`${API}/patients/${id}/biometrics?days=${days}`, getAuthHeaders()),
  getPatientNotes: (id) => axios.get(`${API}/patients/${id}/notes`, getAuthHeaders()),
  addPatientNote: (id, note) => axios.post(`${API}/patients/${id}/notes`, { patient_id: id, note }, getAuthHeaders()),
  
  // Digital Twin
  getDTSBreakdown: (id) => axios.get(`${API}/patients/${id}/dts-breakdown`, getAuthHeaders()),
  getRedFlags: (id) => axios.get(`${API}/patients/${id}/red-flags`, getAuthHeaders()),
  getCoachMessages: (id, limit = 5) => axios.get(`${API}/patients/${id}/coach360?limit=${limit}`, getAuthHeaders()),
  getHabitData: (id) => axios.get(`${API}/patients/${id}/habits`, getAuthHeaders()),
  getEngagement: (id) => axios.get(`${API}/patients/${id}/engagement`, getAuthHeaders()),

  // Analytics
  getAnalyticsSummary: () => axios.get(`${API}/analytics/summary`, getAuthHeaders()),
  getPriorityPatients: () => axios.get(`${API}/analytics/priority-patients`, getAuthHeaders()),

  // CareTeam
  getCohortAnalytics: () => axios.get(`${API}/careteam/cohort-analytics`, getAuthHeaders()),
  getTriageQueue: () => axios.get(`${API}/careteam/triage-queue`, getAuthHeaders()),
  getTasks: (status) => axios.get(`${API}/careteam/tasks${status ? `?status=${status}` : ''}`, getAuthHeaders()),
  getPatientSDOH: (id) => axios.get(`${API}/patients/${id}/sdoh`, getAuthHeaders()),
  getPatientPlans: (id) => axios.get(`${API}/patients/${id}/plans`, getAuthHeaders()),
  updatePatientInsurance: (id, insuranceProvider) => axios.patch(`${API}/patients/${id}/insurance`, { insurance_provider: insuranceProvider }, getAuthHeaders()),
  updatePatientConditions: (id, conditionTags) => axios.patch(`${API}/patients/${id}/conditions`, { condition_tags: conditionTags }, getAuthHeaders()),

  // Messaging System
  getMessageTemplates: () => axios.get(`${API}/messages/templates`, getAuthHeaders()),
  getMessageThreads: () => axios.get(`${API}/messages/threads`, getAuthHeaders()),
  getPatientMessages: (patientId) => axios.get(`${API}/messages/thread/${patientId}`, getAuthHeaders()),
  sendMessage: (data) => axios.post(`${API}/messages`, data, getAuthHeaders()),
  markMessageRead: (messageId) => axios.patch(`${API}/messages/${messageId}/read`, {}, getAuthHeaders()),
  getLegacyMessages: (patientId) => axios.get(`${API}/careteam/messages${patientId ? `?patient_id=${patientId}` : ''}`, getAuthHeaders()),

  // Nudge System
  getNudgeTemplates: () => axios.get(`${API}/nudges/templates`, getAuthHeaders()),
  getNudges: (patientId, status) => {
    let params = [];
    if (patientId) params.push(`patient_id=${patientId}`);
    if (status) params.push(`status=${status}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    return axios.get(`${API}/nudges${query}`, getAuthHeaders());
  },
  sendNudge: (data) => axios.post(`${API}/nudges`, data, getAuthHeaders()),
  sendBatchNudges: (patientIds, nudgeType, customMessage) => 
    axios.post(`${API}/nudges/batch`, { patient_ids: patientIds, nudge_type: nudgeType, custom_message: customMessage }, getAuthHeaders()),
  getNudgeStats: () => axios.get(`${API}/nudges/stats`, getAuthHeaders()),

  // Seed data
  seedData: () => axios.post(`${API}/seed-data`),

  // Members Page Enhanced
  getMembersStats: (periodDays = 7) => axios.get(`${API}/members/stats?period_days=${periodDays}`, getAuthHeaders()),
  getEnhancedMembers: () => axios.get(`${API}/members/enhanced`, getAuthHeaders()),

  // Cohorts
  getCohorts: () => axios.get(`${API}/cohorts`, getAuthHeaders()),
  getCohort: (id) => axios.get(`${API}/cohorts/${id}`, getAuthHeaders()),
  createCohort: (data) => axios.post(`${API}/cohorts`, data, getAuthHeaders()),
  updateCohort: (id, data) => axios.put(`${API}/cohorts/${id}`, data, getAuthHeaders()),
  deleteCohort: (id) => axios.delete(`${API}/cohorts/${id}`, getAuthHeaders()),
  getCohortMembers: (id) => axios.get(`${API}/cohorts/${id}/members`, getAuthHeaders()),
  previewCohort: (filters) => axios.post(`${API}/cohorts/preview`, filters, getAuthHeaders()),

  // Coach360 Activities
  startCoach360Activity: (patientId, activityType, activityName) => 
    axios.post(`${API}/patients/${patientId}/coach360/activity/start`, { activity_type: activityType, activity_name: activityName }, getAuthHeaders()),
  completeCoach360Activity: (patientId, activityId) => 
    axios.post(`${API}/patients/${patientId}/coach360/activity/${activityId}/complete`, {}, getAuthHeaders()),
  getCoach360Activities: (patientId, limit = 10) => 
    axios.get(`${API}/patients/${patientId}/coach360/activities?limit=${limit}`, getAuthHeaders()),
  respondToCoach360: (patientId, messageId, response) => 
    axios.post(`${API}/patients/${patientId}/coach360/respond?message_id=${messageId}&response=${response}`, {}, getAuthHeaders()),

  // Adherence Calendar
  getPatientAdherence: (patientId, weekOffset = 0) => 
    axios.get(`${API}/patients/${patientId}/adherence?week_offset=${weekOffset}`, getAuthHeaders()),
  logAdherence: (patientId, goalType, date, status) => 
    axios.post(`${API}/patients/${patientId}/adherence/log?goal_type=${goalType}&date=${date}&status=${status}`, {}, getAuthHeaders()),

  // Member Actions
  escalateMember: (patientId, reason, assignedTo) => 
    axios.post(`${API}/patients/${patientId}/actions/escalate`, { action_type: 'escalate', reason, assigned_to: assignedTo }, getAuthHeaders()),
  assignCoach: (patientId, coachName) => 
    axios.post(`${API}/patients/${patientId}/actions/assign-coach`, { action_type: 'assign_coach', assigned_to: coachName }, getAuthHeaders()),
  deactivateMember: (patientId, reason) => 
    axios.post(`${API}/patients/${patientId}/actions/deactivate`, { action_type: 'deactivate', reason }, getAuthHeaders()),
  reactivateMember: (patientId) => 
    axios.post(`${API}/patients/${patientId}/actions/reactivate`, {}, getAuthHeaders()),
};
